import { defaultVariableDefinition } from 'pages/AutomationEngine/steps';
import type {
  UiVariable,
  ApiVariable,
  ApiVariableSourcesValue,
  ApiDeletedOption,
  SourceVariableType,
  ApiVariableSource,
  ApiStaticValue,
  LoggedActivityVariableSourceSubTypes,
} from 'pages/AutomationEngine/dialogs/VariableWizard/types';
import {
  AutomationVariableDataType,
  DeletedOption,
  KizenFieldType,
} from 'pages/AutomationEngine/types';
import { GenericOption } from 'pages/AutomationEngine/types';
import {
  MessageDictionary,
  AddErrorMessage,
} from 'pages/AutomationEngine/messageDictionary';

const addDeleted = (label: string, deleted: boolean | undefined) =>
  deleted ? `${label} [Deleted]` : label;

const deletedOptionForFlow = (
  option: DeletedOption,
  addErrorMessage: AddErrorMessage,
  errorMessage: string
) => {
  if (option.deleted) {
    addErrorMessage(errorMessage);
  }

  return {
    ...option,
    label: addDeleted(option.label, option.deleted),
  };
};

const transformApiLoggedActivityValueToDeletedOption = ({
  id,
  description,
  displayName,
  objectName,
  deleted,
}: ApiStaticValue) => ({
  value: id,
  label: displayName || objectName || description || '',
  deleted,
});

// Static Value Translators

const transformApiValueCategoryToDeletedOption = ({
  id,
  objectName,
  description,
  deleted,
}: ApiStaticValue) => ({
  value: id,
  label: description || objectName || '',
  deleted: Boolean(deleted),
});

const transformApiDataSubtype = ({
  id,
  name,
  description,
  deleted,
  objectName,
  entityName,
  ...rest
}: ApiStaticValue): DeletedOption & {
  objectName?: string;
  entityName?: string;
} => {
  return {
    label: description || objectName || name || '',
    value: id,
    deleted: !!deleted,
    objectName,
    entityName,
  };
};

const isStringArrayValue = (
  arr: string[] | ApiStaticValue[]
): arr is string[] => typeof arr[0] === 'string';

const translateObjectToDeletedOption = (
  value: ApiStaticValue | undefined,
  transX: (value: ApiStaticValue) => DeletedOption,
  addErrorMessage: AddErrorMessage,
  errorMessage: string
): DeletedOption | undefined => {
  if (!value) {
    return value;
  }

  return deletedOptionForFlow(transX(value), addErrorMessage, errorMessage);
};

const translateToDeletedOption = (
  value: ApiVariableSourcesValue | undefined,
  transX: (value: ApiStaticValue) => DeletedOption,
  addErrorMessage: AddErrorMessage,
  errorMessage: string
): DeletedOption | DeletedOption[] | string | string[] | undefined => {
  if (Array.isArray(value)) {
    if (isStringArrayValue(value)) {
      return value;
    }
    return value.map((v) =>
      deletedOptionForFlow(transX(v), addErrorMessage, errorMessage)
    );
  }

  if (!value || typeof value === 'string') {
    return value;
  }

  return deletedOptionForFlow(transX(value), addErrorMessage, errorMessage);
};

const valueCategoryForApi = (
  valueCategory: GenericOption | string | undefined
): string | undefined => {
  if (!valueCategory || typeof valueCategory === 'string') {
    return valueCategory;
  }

  return valueCategory?.value;
};

export const mapApiSource = (source: SourceVariableType, isArray: boolean) => {
  switch (source.sourceType) {
    case 'static': {
      const { staticVariable } = source;
      return {
        sourceType: source.sourceType,
        sourceSubtype: source.sourceSubtype,
        value: Array.isArray(staticVariable?.value)
          ? staticVariable.value.map((v) =>
              typeof v === 'string' ? v : v.value
            )
          : isArray
            ? []
            : staticVariable?.value && typeof staticVariable.value === 'object'
              ? staticVariable?.value?.value
              : staticVariable?.value ?? '',
        valueCategory: valueCategoryForApi(staticVariable?.valueCategory),
        valueHtml: staticVariable?.valueHtml,
        arraySeparator: staticVariable?.arraySeparator,
      };
    }
    case 'activity_past_due_trigger': {
      const { activityPassedDueVariable } = source;
      return {
        sourceType: source.sourceType,
        sourceSubtype: valueCategoryForApi(
          activityPassedDueVariable?.sourceSubtype
        ),
        value: activityPassedDueVariable?.value
          ? valueCategoryForApi(activityPassedDueVariable?.value)
          : null,

        valueCategory: valueCategoryForApi(
          activityPassedDueVariable?.valueCategory
        ),
      };
    }
    case 'logged_activity_trigger': {
      const { loggedActivityVariable } = source;
      return {
        sourceType: source.sourceType,
        sourceSubtype: valueCategoryForApi(
          loggedActivityVariable?.sourceSubtype
        ),
        value: loggedActivityVariable?.value
          ? valueCategoryForApi(loggedActivityVariable?.value)
          : null,

        valueCategory: valueCategoryForApi(
          loggedActivityVariable?.valueCategory
        ),
      };
    }
    case 'field_value': {
      const { fieldVariable } = source;
      return {
        sourceType: source.sourceType,
        sourceSubtype: valueCategoryForApi(fieldVariable?.sourceSubtype),
        field: valueCategoryForApi(fieldVariable?.field),
        relationshipField: valueCategoryForApi(
          fieldVariable?.relationshipField
        ),
      };
    }
    case 'variable': {
      return {
        sourceType: source.sourceType,
        variable:
          typeof source.automationVariable?.value === 'object'
            ? source.automationVariable?.value?.label
            : source.automationVariable?.value,
      };
    }
    case 'trigger_time': {
      return {
        sourceType: source.sourceType,
      };
    }
    case 'webhook_trigger': {
      const { webhookVariable } = source;
      return {
        sourceType: source.sourceType,
        value: webhookVariable?.value?.value || null,
        valueCategory: webhookVariable?.valueCategory?.value || null,
      };
    }
    default:
      return {};
  }
};

export const variableForApi = (
  currentVariables: any[],
  uiVariable: UiVariable,
  order: number
) => {
  if (uiVariable.type === 'initialize_variable') {
    const result = { ...uiVariable, order };
    const { details: orgDetails, id, considerEmptyAnError, ...rest } = result;

    const sources = (orgDetails.sources || []).map((s) =>
      mapApiSource(s, orgDetails.variable?.isArray)
    );

    const dataSubtype =
      orgDetails.variable.dataSubtype &&
      typeof orgDetails.variable.dataSubtype === 'object'
        ? orgDetails.variable?.dataSubtype.value
        : orgDetails.variable?.dataSubtype;

    const details = {
      isRequired: considerEmptyAnError,
      variable: { ...orgDetails.variable, dataSubtype },
      arrayAggregationMode: orgDetails.arrayAggregationMode,
      deduplicateArray: orgDetails.deduplicateArray || false,
      sources,
    };

    currentVariables.push({
      // we need id especially to update action and if it's not a new trigger
      ...(!id.startsWith('new') && { id }),
      [`action_initialize_variable`]: details,
      key: id,
      ...rest,
    });
  }
  return currentVariables;
};

const getDeletedOption = (
  staticValue: ApiStaticValue,
  addErrorMessage: AddErrorMessage,
  sourceSubtype: AutomationVariableDataType,
  messageDictionary: MessageDictionary
): DeletedOption => {
  if (staticValue.deleted) {
    switch (sourceSubtype) {
      case 'entity':
        addErrorMessage(messageDictionary.objectRecordHasBeenDeleted);
        break;

      case 'employee':
        addErrorMessage(messageDictionary.teamMemberHasBeenDeleted);
        break;

      default:
        addErrorMessage(messageDictionary.fieldDeleted);
        break;
    }
  }

  return {
    value: staticValue.id,
    label: addDeleted(staticValue.displayName, staticValue.deleted),
    deleted: staticValue.deleted,
  };
};

const getStaticVariableValue = (
  staticValue: ApiVariableSourcesValue,
  addErrorMessage: AddErrorMessage,
  sourceSubtype: AutomationVariableDataType,
  messageDictionary: MessageDictionary
):
  | string
  | string[]
  | (GenericOption & { entityId?: string; fieldType?: KizenFieldType })
  | Array<GenericOption & { entityId?: string; fieldType?: KizenFieldType }>
  | undefined => {
  if (Array.isArray(staticValue)) {
    if (isStringArrayValue(staticValue)) {
      return staticValue;
    }
    return staticValue.map((v) =>
      getDeletedOption(v, addErrorMessage, sourceSubtype, messageDictionary)
    );
  }

  if (!staticValue || typeof staticValue === 'string') {
    return staticValue;
  }

  return getDeletedOption(
    staticValue,
    addErrorMessage,
    sourceSubtype,
    messageDictionary
  );
};

const optionForUI = (
  option: ApiDeletedOption | string | undefined,
  addErrorMessage: AddErrorMessage,
  errorMessage: string
) => {
  if (!option || typeof option === 'string') {
    return option;
  }

  if (option.deleted) {
    addErrorMessage(errorMessage);
  }

  return {
    value: option.id,
    label: addDeleted(option.name, option.deleted),
    deleted: option.deleted,
  };
};

export const mapFlowSource =
  (
    variableId: string,
    messageDictionary: MessageDictionary,
    addErrorMessage: any
  ) =>
  (source: ApiVariableSource, index: number) => {
    const {
      sourceType,
      sourceSubtype,
      value,
      valueHtml,
      valueCategory,
      field,
      relationshipField,
      arraySeparator,
    } = source;

    const sourceId = `${variableId}-${index}`;
    switch (sourceType) {
      case 'static':
        return {
          id: sourceId,
          sourceErrors: undefined,
          sourceType,
          sourceSubtype,
          staticVariable: {
            valueCategory: translateObjectToDeletedOption(
              valueCategory,
              transformApiValueCategoryToDeletedOption,
              addErrorMessage,
              sourceSubtype === 'entity'
                ? messageDictionary.objectHasBeenDeleted
                : messageDictionary.fieldDeleted
            ),
            value: getStaticVariableValue(
              value,
              addErrorMessage,
              sourceSubtype,
              messageDictionary
            ),
            valueHtml,
            sourceSubtype,
            arraySeparator,

            variableType: sourceType,
            isValid: true,
          },
        };

      case 'logged_activity_trigger':
      case 'activity_past_due_trigger':
        return {
          id: sourceId,
          sourceErrors: undefined,
          sourceType,
          sourceSubtype,
          [sourceType === 'logged_activity_trigger'
            ? 'loggedActivityVariable'
            : 'activityPassedDueVariable']: {
            sourceSubtype,
            valueCategory: optionForUI(
              valueCategory as unknown as ApiDeletedOption,
              addErrorMessage,
              messageDictionary.activityDeleted
            ),
            value: translateToDeletedOption(
              value,
              transformApiLoggedActivityValueToDeletedOption,
              addErrorMessage,
              (sourceSubtype as LoggedActivityVariableSourceSubTypes) ===
                'record_association'
                ? messageDictionary.activityFieldValueDeleted
                : messageDictionary.associatedObjectDeleted
            ),
            variableType: sourceType,
            isValid: true,
          },
        };

      case 'field_value':
        return {
          id: sourceId,
          sourceErrors: undefined,
          sourceType,
          sourceSubtype,
          fieldVariable: {
            sourceSubtype,
            variableType: sourceType,
            field: translateToDeletedOption(
              field,
              transformApiLoggedActivityValueToDeletedOption,
              addErrorMessage,
              messageDictionary.fieldDeleted
            ),
            relationshipField: translateToDeletedOption(
              relationshipField,
              transformApiLoggedActivityValueToDeletedOption,
              addErrorMessage,
              messageDictionary.fieldDeleted
            ),
            isValid: true,
          },
        };

      case 'variable': {
        const { variable } = source;

        return {
          id: sourceId,
          sourceErrors: undefined,
          sourceType,
          sourceSubtype,
          automationVariable: {
            ...variable,
            isValid: true,
            value: {
              label: variable.name,
              value: variable.name,
              meta: {
                dataType: sourceType,
                dataSubtype: sourceSubtype,
                sourceType,
              },
            },
          },
        };
      }
      case 'trigger_time': {
        return {
          id: sourceId,
          sourceErrors: undefined,
          sourceType,
          sourceSubtype,
        };
      }
      case 'webhook_trigger': {
        return {
          id: sourceId,
          sourceErrors: undefined,
          sourceType,
          sourceSubtype,
          webhookVariable: {
            valueCategory: translateToDeletedOption(
              valueCategory!,
              (option) => ({
                value: option?.value || '',
                label: option?.label || '',
                deleted: !!option.deleted,
              }),
              addErrorMessage,
              messageDictionary.webhookTriggerDeleted
            ),
            value: translateToDeletedOption(
              value,
              (option) => ({
                value: option?.value || '',
                label: option?.label || '',
                deleted: !!option.deleted,
              }),
              addErrorMessage,
              messageDictionary.webhookTriggerValueDeleted
            ),
            variableType: sourceType,
            isValid: true,
          },
        };
      }
      default:
        return { ...source, id: sourceId, sourceErrors: undefined };
    }
  };

export const variablesForFlow = (
  apiVariables: ApiVariable[],
  messageDictionary: MessageDictionary
) => {
  const variablesWithErrors = apiVariables.map((variable, order) => {
    const variableErrors: string[] = [];
    const addErrorMessage = (message: string) => {
      variableErrors.push(message);
    };

    const { actionInitializeVariable, ...rest } = variable;
    const sources = actionInitializeVariable.sources.map(
      mapFlowSource(variable?.id, messageDictionary, addErrorMessage)
    );
    if (!actionInitializeVariable?.variable) {
      addErrorMessage(messageDictionary.automationFailedToLoad);
    }
    const dataSubtype = translateToDeletedOption(
      actionInitializeVariable?.variable?.dataSubtype,
      transformApiDataSubtype,
      addErrorMessage,
      messageDictionary.objectHasBeenDeleted
    );
    return {
      variable: {
        ...rest,
        type: variable.stepType,
        order,
        details: {
          ...actionInitializeVariable,
          variable: { ...actionInitializeVariable.variable, dataSubtype },
          sources,
        },
        hasError: variableErrors.length ? variableErrors : false,
        considerEmptyAnError: actionInitializeVariable.isRequired,
      },
      arrayAggregationMode: actionInitializeVariable.arrayAggregationMode,
      deduplicateArray: actionInitializeVariable.deduplicateArray || false,
      error: variableErrors.length
        ? { id: variable.id, errors: variableErrors }
        : null,
    };
  });

  const justTheVariables = variablesWithErrors.map(({ variable }) => variable);

  let varParent: null | string = null;
  const regularVariables = justTheVariables
    .filter((v) => !v.details.variable.isArrayItemVariable)
    .map((v, order) => {
      const next = { ...v, parentStepId: varParent, order };
      varParent = v.id;
      return next;
    });

  let itemVarParent = defaultVariableDefinition.id;
  const itemVariables = justTheVariables
    .filter((v) => v.details.variable.isArrayItemVariable)
    .map((v, order) => {
      const next = {
        ...v,
        parentStepId: itemVarParent,
        order: order + regularVariables.length + 1,
      };
      itemVarParent = v.id;
      return next;
    });

  return {
    variables: [
      ...regularVariables,
      // The add-variable placeholder sits between the regular variables and the
      // item variables, so it follows the last regular one. Hanging it off the
      // last *item* variable pointed it at itself when there were none, and
      // closed a cycle with them when there were.
      {
        ...defaultVariableDefinition,
        order: regularVariables.length,
        parentStepId: varParent,
      },
      ...itemVariables,
    ],
    variableErrors: variablesWithErrors
      .filter(({ error }) => !!error)
      .map(({ error }) => error),
  };
};
